import {
  SafeParseReturnType,
  z,
  ZodAny,
  ZodDate,
  ZodEnum,
  ZodNumber,
  ZodString,
  ZodType,
} from "zod";
import {
  DisplayablePath,
  displayablePathToDisplayValue,
  ErrorMessageObject,
  listIndex,
  messageId,
  MessageId,
  ValidationError,
  validationErrorFromInternal,
  ValidationErrorInternal,
  ValidationState,
  ValidationStateOkOrError,
} from "../types";
import log from "loglevel";
import { isNullish } from "../util/common-util";
import { typedObjectEntries } from "../util/common-object-util";
import * as jsonpatch from "fast-json-patch";
import { Operation } from "fast-json-patch";
import { predefinedMessageIds } from "../../predefined-message-ids";

/**
 * エンティティや複合値オブジェクトの項目のメタデータ。
 * 項目名、null許可、値オブジェクトメタデータを含む。
 */
export class PropertyMeta<
  ValueMeta extends ValueObjectMeta<ZodType<any>, ZodType<any>, ZodType<any>>,
> {
  constructor(
    /**
     * 項目物理名。
     */
    readonly name: string,
    /**
     * 項目論理名。
     */
    readonly logicalName: string,
    /**
     * 項目名を表すメッセージID。
     */
    readonly messageId: MessageId,

    readonly required: "always" | "afterSave" | "none",
    /**
     * リストかどうか。
     * trueならこの項目はリスト項目、falseなら通常の単項目である。
     */
    readonly isList: boolean,
    /**
     * この項目の値の、値オブジェクトメタデータ。
     */
    readonly valueMeta: ValueMeta,
  ) {}
}

export abstract class ObjectMeta<
  Schema extends ZodType<any>,
  JsonSchema extends ZodType<any>,
> {
  /**
   * 物理名。
   */
  abstract readonly name: string;
  /**
   * 論理名。
   */
  abstract readonly logicalName: string;
  /**
   * オブジェクト名を表すメッセージID。
   */
  abstract readonly messageId: MessageId;
  /**
   * 最小限のスキーマ。
   * 基本的には用いないが、下書き時など、フルのバリデーションを行いたくないときに利用する。
   * validate関数で skipOptional = true を指定したときは、これが用いられる。
   */
  abstract readonly minimalSchema: Schema;
  /**
   * スキーマ。
   * validate関数で skipOptional = false を指定したときは、これが用いられる。
   */
  abstract readonly schema: Schema;
  /**
   * JSONスキーマ。
   * バックエンドへのリクエストやレスポンスで使う型。
   */
  abstract readonly jsonSchema: JsonSchema;

  /**
   * 自オブジェクトの各項目の 項目名 : メタ の一覧。
   * オブジェクト型の場合のみ値が存在する。
   * カスタムバリデーションにて用いる。
   */
  abstract readonly propertyMetas:
    | Record<
        string,
        | PropertyMeta<
            ValueObjectMeta<ZodType<any>, ZodType<any>, ZodType<any>>
          >
        | undefined
      >
    | undefined;
}

/**
 * ドメインオブジェクトのメタデータ。
 *
 * スキーマ(型情報とチェック仕様)、デフォルトプレースホルダ文字列を情報として含む。
 * また、バリデーション関数を提供する。
 */
export abstract class DomainObjectMeta<
  Schema extends ZodType<any>,
  SavedSchema extends ZodType<any>,
  JsonSchema extends ZodType<any>,
> extends ObjectMeta<Schema, JsonSchema> {
  /**
   * 保存済の最小限のスキーマ。
   */
  abstract readonly savedMinimalSchema: SavedSchema;
  /**
   * 保存済のスキーマ。
   */
  abstract readonly savedSchema: SavedSchema;

  /**
   * プレースホルダ文字列を示すメッセージID。
   * 未指定の場合、空文字列を指定したのと同じ扱いとなる。
   */
  abstract readonly placeholder?: MessageId;

  /**
   * カスタムバリデーション関数。
   * zodで表現可能なスキーマだけでは網羅できないバリデーションが必要な場合、これを渡す。
   * 値がある場合、validate関数内でスキーマによるバリデーションの後に実行される。
   */
  protected abstract readonly customValidate?: (
    value: z.infer<Schema> | z.infer<SavedSchema>,
    skipOptional: boolean,
  ) => ErrorMessageObject[];

  protected recursiveCustomValidate(
    currentPath: (string | number)[],
    value: z.infer<Schema> | z.infer<SavedSchema>,
    skipOptional: boolean,
  ): ValidationErrorInternal[] {
    // プロパティのカスタムバリデーション
    const childrenErrors: ValidationErrorInternal[] = typedObjectEntries(
      this.propertyMetas ?? {},
    ).flatMap(([key, propertyMeta]) => {
      if (isNullish(propertyMeta)) return [];
      const valueObjectMeta = propertyMeta.valueMeta;

      const propertyValue = (value as any)[key];
      if (isNullish(propertyValue)) return [];

      if (propertyMeta.isList) {
        return propertyValue.flatMap((elementValue: any, idx: number) => {
          return valueObjectMeta.recursiveCustomValidate(
            [...currentPath, propertyMeta.name, idx],
            elementValue,
            skipOptional,
          );
        });
      } else {
        return valueObjectMeta.recursiveCustomValidate(
          [...currentPath, propertyMeta.name],
          propertyValue,
          skipOptional,
        );
      }
    });

    // 自分のカスタムバリデーション
    const selfCustomValidateResult: ValidationErrorInternal[] =
      this.customValidate?.(value, skipOptional)?.map((errorMessageObject) => ({
        path: currentPath,
        errorMessageObject,
      })) ?? [];

    return [...childrenErrors, ...selfCustomValidateResult];
  }

  /**
   * 値が、保存前の値に対するチェック仕様(スキーマ + カスタムバリデーション)に合うことを検証する。
   * @param value バリデーション対象の値。
   * @param skipOptional trueなら最小限のバリデーションのみを行う。
   */
  validate(
    value: any,
    skipOptional: boolean = false,
  ): ValidationStateOkOrError<z.infer<Schema>> {
    const zodResult = skipOptional
      ? this.minimalSchema.safeParse(value)
      : this.schema.safeParse(value);
    const errorsFromZod = zodResultToValidationErrors(
      this.name,
      this.logicalName,
      zodResult,
      this.propertyMetas,
    );
    // Zodによるエラーが発生した場合、この時点で終了。
    if (errorsFromZod.length > 0)
      return {
        state: "error",
        errors: errorsFromZod,
      };
    if (!zodResult.success) throw new Error("unreachable");

    // オブジェクトではなければ、ここで正常終了。
    if (
      typeof value === "string" ||
      typeof value === "number" ||
      typeof value === "boolean"
    )
      return {
        state: "ok",
        value: zodResult.data,
      };

    const errorsFromCustomValidate = this.recursiveCustomValidate(
      [],
      value,
      skipOptional,
    );
    if (errorsFromCustomValidate.length > 0)
      return {
        state: "error",
        errors: errorsFromCustomValidate.map((e) =>
          validationErrorFromInternal(
            this.name,
            this.logicalName,
            e,
            this.propertyMetas,
          ),
        ),
      };

    return {
      state: "ok",
      value: zodResult.data,
    };
  }

  /**
   * 値が、保存済の値に対するチェック仕様(スキーマ + カスタムバリデーション)に合うことを検証する。
   * @param value バリデーション対象の値。
   * @param skipOptional trueなら最小限のバリデーションのみを行う。
   */
  validateAsSaved(
    value: any,
    skipOptional: boolean = false,
  ): ValidationState<z.infer<SavedSchema>> {
    const zodResult = skipOptional
      ? this.minimalSchema.safeParse(value)
      : this.schema.safeParse(value);
    const errorsFromZod = zodResultToValidationErrors(
      this.name,
      this.logicalName,
      zodResult,
      this.propertyMetas,
    );
    // Zodによるエラーが発生した場合、この時点で終了。
    if (errorsFromZod.length > 0)
      return {
        state: "error",
        errors: errorsFromZod,
      };
    if (!zodResult.success) throw new Error("unreachable");

    // オブジェクトではなければ、ここで正常終了。
    if (
      typeof value === "string" ||
      typeof value === "number" ||
      typeof value === "boolean"
    )
      return {
        state: "ok",
        value: zodResult.data,
      };

    const errorsFromCustomValidate = this.recursiveCustomValidate(
      [],
      value,
      skipOptional,
    );
    if (errorsFromCustomValidate.length > 0)
      return {
        state: "error",
        errors: errorsFromCustomValidate.map((e) =>
          validationErrorFromInternal(this.name, this.logicalName, e),
        ),
      };

    return {
      state: "ok",
      value: zodResult.data,
    };
  }

  /**
   * 値を、未保存のドメインオブジェクトに変換できる場合、変換して返す。
   * できない場合、nullを返す。
   */
  toDomainObjectOrNull(
    value: any,
    skipOptional: boolean = false,
  ): z.infer<Schema> | null {
    const validationResult = this.validate(value, skipOptional);
    if (validationResult.state === "ok") return validationResult.value;
    log.debug(
      `toDomainObjectOrNull: conversion failed: validationResult=${JSON.stringify(
        validationResult,
      )}, value=`,
      value,
    );
    return null;
  }

  /**
   * 値を、保存済のドメインオブジェクトに変換できる場合、変換して返す。
   * できない場合、nullを返す。
   */
  toSavedDomainObjectOrNull(
    value: any,
    skipOptional: boolean = false,
  ): z.infer<SavedSchema> | null {
    const validationResult = this.validateAsSaved(value, skipOptional);
    if (validationResult.state === "ok") return validationResult.value;
    log.debug(
      `toSavedDomainObjectOrNull: conversion failed: validationResult=${JSON.stringify(
        validationResult,
      )}, value=`,
      value,
    );
    return null;
  }

  toJsonObjectOrNull(value: any): z.infer<JsonSchema> | null {
    const result = this.jsonSchema.safeParse(value);
    if (result.success) return result.data;
    log.debug(
      `toJsonObjectOrNull: conversion failed: result=${JSON.stringify(
        result,
      )}, value=`,
      value,
    );
    return null;
  }

  toJsonPatchOperationsOrNull(
    before: z.infer<Schema>,
    after: z.infer<Schema>,
  ): Operation[] | null {
    const beforeJson = this.toJsonObjectOrNull(before);
    if (isNullish(beforeJson)) {
      log.debug(
        `toDiffUpdateRequestOrNull: beforeの値が不正なため、失敗しました。 name=${this.name}`,
      );
      return null;
    }

    const afterJson = this.toJsonObjectOrNull(after);
    if (isNullish(afterJson)) {
      log.debug(
        `toDiffUpdateRequestOrNull: afterの値が不正なため、失敗しました。 name=${this.name}`,
      );
      return null;
    }

    return jsonpatch.compare(beforeJson, afterJson);
  }
}

/**
 * エンティティのメタデータ。
 *
 * スキーマ(型情報とチェック仕様)、デフォルトプレースホルダ文字列を情報として含む。
 * また、バリデーション関数を提供する。
 */
export class EntityMeta<
  Schema extends ZodType<any>,
  SavedSchema extends ZodType<any>,
  JsonSchema extends ZodType<any>,
> extends DomainObjectMeta<Schema, SavedSchema, JsonSchema> {
  constructor(
    override readonly name: string,
    override readonly logicalName: string,
    override readonly messageId: MessageId,
    override readonly minimalSchema: Schema,
    override readonly savedMinimalSchema: SavedSchema,
    override readonly schema: Schema,
    override readonly savedSchema: SavedSchema,
    override readonly jsonSchema: JsonSchema,
    override readonly placeholder: MessageId | undefined,
    protected override readonly customValidate:
      | ((
          value: z.infer<Schema>,
          skipOptional: boolean,
        ) => ErrorMessageObject[])
      | undefined,
    override readonly propertyMetas: Record<
      keyof z.infer<Schema>,
      PropertyMeta<any> | undefined
    >,
  ) {
    super();
  }
}

/**
 * 値オブジェクトのメタデータ。
 *
 * スキーマ(型情報とチェック仕様)、デフォルトプレースホルダ文字列を情報として含む。
 * また、バリデーション関数を提供する。
 */
export abstract class ValueObjectMeta<
  Schema extends ZodType<any>,
  SavedSchema extends ZodType<any>,
  JsonSchema extends ZodType<any>,
> extends DomainObjectMeta<Schema, SavedSchema, JsonSchema> {}

/**
 * 文字列値オブジェクトのメタデータ。
 *
 * スキーマ(型情報とチェック仕様)、デフォルトプレースホルダ文字列を情報として含む。
 * また、バリデーション関数を提供する。
 */
export class ValueObjectMetaString extends ValueObjectMeta<
  ZodString,
  ZodString,
  ZodString
> {
  constructor(
    override readonly name: string,
    override readonly logicalName: string,
    override readonly messageId: MessageId,
    override readonly minimalSchema: ZodString,
    override readonly schema: ZodString,
    override readonly jsonSchema: ZodString,
    override readonly placeholder?: MessageId,
    protected override readonly customValidate?: (
      value: string,
      skipOptional: boolean,
    ) => ErrorMessageObject[],
  ) {
    super();
    this.partial = new ValueObjectMetaStringPartial(
      name,
      logicalName,
      messageId,
      placeholder,
    );
  }

  override readonly savedMinimalSchema = this.minimalSchema;
  override readonly savedSchema = this.schema;
  override readonly propertyMetas: undefined = undefined;

  readonly partial: ValueObjectMetaStringPartial;
}

/**
 * 文字列値オブジェクトの部分文字列のメタデータ。
 * 検索条件などに用いる。
 */
export class ValueObjectMetaStringPartial extends ValueObjectMeta<
  ZodString,
  ZodString,
  ZodString
> {
  constructor(
    override readonly name: string,
    override readonly logicalName: string,
    override readonly messageId: MessageId,

    override readonly placeholder?: MessageId,
  ) {
    super();
  }
  override readonly minimalSchema: ZodString = z.string().min(1).max(1000);
  override readonly savedMinimalSchema = this.minimalSchema;
  override readonly schema: ZodString = z.string().min(1).max(1000);
  override readonly savedSchema = this.schema;
  override readonly jsonSchema: ZodString = z.string().min(1).max(1000);

  protected override readonly customValidate = undefined;

  override readonly propertyMetas: undefined = undefined;
}

/**
 * enum値オブジェクトのメタデータ。
 *
 * スキーマ(型情報とチェック仕様)、デフォルトプレースホルダ文字列を情報として含む。
 * また、バリデーション関数を提供する。
 */
export class ValueObjectMetaEnum<
  Tuple extends [string, ...string[]],
> extends ValueObjectMeta<ZodEnum<Tuple>, ZodEnum<Tuple>, ZodEnum<Tuple>> {
  constructor(
    override readonly name: string,
    override readonly logicalName: string,
    override readonly messageId: MessageId,
    /**
     * enumとして取りうる値の一覧。
     */
    values: Tuple,
    /**
     * 値からメッセージIDへの紐づけを表す辞書。
     */
    readonly dict: { [key in Tuple[number]]: string },
    override readonly placeholder?: MessageId,
    protected override readonly customValidate?: (
      value: Tuple[number],
      skipOptional: boolean,
    ) => ErrorMessageObject[],
  ) {
    super();

    this.values = values;

    const _schema = z.enum(values);

    this._schema = _schema;

    this.minimalSchema = _schema;
    this.savedMinimalSchema = _schema;
    this.schema = _schema;
    this.savedSchema = _schema;
    this.jsonSchema = _schema;
  }

  /**
   * enumとして取りうる値の一覧。
   */
  readonly values: Tuple[number][];

  private readonly _schema: ZodEnum<Tuple>;

  override readonly minimalSchema: ZodEnum<Tuple>;
  override readonly savedMinimalSchema;
  override readonly schema: ZodEnum<Tuple>;
  override readonly savedSchema;
  override readonly jsonSchema: ZodEnum<Tuple>;
  override readonly propertyMetas: undefined = undefined;

  override validate(
    value: any,
    _skipOptional: boolean = false,
  ): ValidationStateOkOrError<Tuple[number]> {
    const zodResult = this._schema.safeParse(value);
    const errorsFromZod = zodResultToValidationErrors(
      this.name,
      this.logicalName,
      zodResult,
      this.propertyMetas,
    );
    // Zodによるエラーが発生した場合、この時点で終了。
    if (errorsFromZod.length > 0)
      return {
        state: "error",
        errors: errorsFromZod,
      };
    if (!zodResult.success) throw new Error("unreachable");

    return {
      state: "ok",
      value: zodResult.data,
    };
  }
}

/**
 * 数値値オブジェクトのメタデータ。
 *
 * スキーマ(型情報とチェック仕様)、デフォルトプレースホルダ文字列を情報として含む。
 * また、バリデーション関数を提供する。
 */
export class ValueObjectMetaNumber extends ValueObjectMeta<
  ZodNumber,
  ZodNumber,
  ZodNumber
> {
  constructor(
    override readonly name: string,
    override readonly logicalName: string,
    override readonly messageId: MessageId,
    override readonly minimalSchema: ZodNumber,
    override readonly schema: ZodNumber,
    override readonly jsonSchema: ZodNumber,
    override readonly placeholder?: MessageId,
    protected override readonly customValidate?: (
      value: number,
      skipOptional: boolean,
    ) => ErrorMessageObject[],
  ) {
    super();
  }

  override readonly savedMinimalSchema = this.minimalSchema;
  override readonly savedSchema = this.schema;
  override readonly propertyMetas: undefined = undefined;
}

/**
 * 複合値オブジェクトのメタデータ。
 *
 * スキーマ(型情報とチェック仕様)、デフォルトプレースホルダ文字列を情報として含む。
 * また、バリデーション関数を提供する。
 */
export class ValueObjectMetaComposite<
  Schema extends ZodType<any>,
  SavedSchema extends ZodType<any>,
  JsonSchema extends ZodType<any>,
> extends ValueObjectMeta<Schema, SavedSchema, JsonSchema> {
  constructor(
    override readonly name: string,
    override readonly logicalName: string,
    override readonly messageId: MessageId,
    override readonly minimalSchema: Schema,
    override readonly savedMinimalSchema: SavedSchema,
    override readonly schema: Schema,
    override readonly savedSchema: SavedSchema,
    override readonly jsonSchema: JsonSchema,
    override readonly placeholder: MessageId | undefined,
    protected override readonly customValidate:
      | ((
          value: z.infer<Schema>,
          skipOptional: boolean,
        ) => ErrorMessageObject[])
      | undefined,
    override readonly propertyMetas: Record<
      keyof z.infer<Schema>,
      PropertyMeta<any> | undefined
    >,
  ) {
    super();
  }
}

const datetimeJsonSchemaPreprocess = (item: any) => {
  if (item instanceof Date) {
    return item.toISOString();
  }
  return item;
};

/**
 * 定義済日時値オブジェクトのメタデータ。
 *
 * スキーマ(型情報とチェック仕様)、デフォルトプレースホルダ文字列を情報として含む。
 * また、バリデーション関数を提供する。
 */
export class ValueObjectMetaPredefinedDatetime extends ValueObjectMeta<
  ZodDate,
  ZodDate,
  ZodType
> {
  constructor() {
    super();
  }

  override readonly name: string = "datetime";
  override readonly logicalName: string = "日時";
  override readonly messageId: MessageId = messageId(
    predefinedMessageIds.datetime,
  );
  override readonly minimalSchema: ZodDate = z.coerce.date();
  override readonly savedMinimalSchema: ZodDate = this.minimalSchema;
  override readonly schema: ZodDate = z.coerce.date();
  override readonly savedSchema: ZodDate = this.schema;
  override readonly jsonSchema: ZodType = z.preprocess(
    datetimeJsonSchemaPreprocess,
    z
      .string()
      .datetime()
      .nullish()
      .transform((x) => x ?? undefined),
  );
  override readonly placeholder: undefined = undefined;
  protected override readonly customValidate: undefined = undefined;
  override readonly propertyMetas: undefined = undefined;
}

/**
 * 定義済Json値オブジェクトのメタデータ。
 *
 * スキーマ(型情報とチェック仕様)、デフォルトプレースホルダ文字列を情報として含む。
 * また、バリデーション関数を提供する。
 */
export class ValueObjectMetaPredefinedJson extends ValueObjectMeta<
  ZodAny,
  ZodAny,
  ZodType
> {
  constructor() {
    super();
  }

  override readonly name: string = "json";
  override readonly logicalName: string = "JSON";
  override readonly messageId: MessageId = messageId(predefinedMessageIds.json);
  override readonly minimalSchema: ZodAny = z.any();
  override readonly savedMinimalSchema: ZodAny = this.minimalSchema;
  override readonly schema: ZodAny = z.any();
  override readonly savedSchema: ZodAny = this.schema;
  override readonly jsonSchema: ZodType = z
    .any()
    .nullish()
    .transform((x) => x ?? undefined);
  override readonly placeholder: undefined = undefined;
  protected override readonly customValidate: undefined = undefined;
  override readonly propertyMetas: undefined = undefined;
}

/**
 * リクエスト・レスポンスオブジェクトのメタデータ。
 *
 * スキーマ(型情報とチェック仕様)を情報として含む。
 * また、バリデーション関数を提供する。
 */
export class RequestResponseObjectMeta<
  Schema extends ZodType<any>,
  JsonSchema extends ZodType<any>,
> extends ObjectMeta<Schema, JsonSchema> {
  constructor(
    override readonly name: string,
    override readonly logicalName: string,
    override readonly messageId: MessageId,
    override readonly minimalSchema: Schema,
    override readonly schema: Schema,
    override readonly jsonSchema: JsonSchema,
    override readonly propertyMetas: Record<string, PropertyMeta<any>>,
  ) {
    super();
  }
}

function zodResultToValidationErrors(
  objectName: string,
  objectLogicalName: string,
  zodResult: SafeParseReturnType<any, any>,
  propertyMetas: Record<string, PropertyMeta<any> | undefined> | undefined,
): ValidationError[] {
  if (zodResult.success) return [];
  const err = zodResult.error;

  return err.issues.map((issue) => {
    const displayablePath = pathToDisplayablePath(issue.path, propertyMetas);
    // TODO このエラー処理で良いか？どういうときに起きるか？
    if (isNullish(displayablePath))
      throw new Error(`invalid path for propertyMetas: ${issue.path}`);
    return {
      objectName,
      objectLogicalName,
      fullPropertyName: issue.path.join("."),
      fullPropertyDisplayName: displayablePathToDisplayValue(displayablePath),
      errorMessage: issue.message,
    };
  });
}

/**
 * パスを、表示用のパスに変換する。
 * 与えられたmetaと不整合なパスだった場合、nullを返す。
 */
export function pathToDisplayablePath(
  path: (string | number)[],
  propertyMetas: Record<
    string,
    | PropertyMeta<ValueObjectMeta<ZodType<any>, ZodType<any>, ZodType<any>>>
    | undefined
  > = {},
): DisplayablePath | null {
  let currentPropertyMetas: Record<
    string,
    | PropertyMeta<ValueObjectMeta<ZodType<any>, ZodType<any>, ZodType<any>>>
    | undefined
  > = propertyMetas;
  const displayablePath: DisplayablePath = [];
  for (let i = 0; i < path.length; i++) {
    const p = path[i];

    // 配列のインデクスだった場合は、格納してから次へ。
    if (typeof p === "number") {
      displayablePath.push(listIndex(p));
      continue;
    }

    const propertyMeta = currentPropertyMetas[p];
    if (isNullish(propertyMeta)) return null;
    displayablePath.push(propertyMeta.messageId);

    // 最後ならループを抜ける。
    if (i === path.length - 1) break;

    const nextPropertyMetas = propertyMeta.valueMeta.propertyMetas;
    if (isNullish(nextPropertyMetas)) return null;

    currentPropertyMetas = nextPropertyMetas;
  }
  return displayablePath;
}
